import { API } from '../../api';
import { Alert, Button, Col, Form, Input, Modal, Row, message } from 'antd';
import { isEmpty, upperFirst } from 'lodash';
import React, { useState } from 'react';

message.config({
  duration: 3,
  maxCount: 3,
  rtl: false,
});

const CreateDataContractModal = ({ isOpen, closeModal, refreshDataContracts }) => {
  const [form] = Form.useForm();
  const [validationErrors, setValidationErrors] = useState({});
  const { TextArea } = Input;

  const handleModalClose = () => {
    form.resetFields();
    closeModal();
  };

  const createDataContract = async params => {
    try {
      setValidationErrors({});
      await API.post('/dataContract', { dataContract: params });
      message.success(`${upperFirst(params.name)} Data Contract Created!`);

      await refreshDataContracts();
      handleModalClose();
    } catch (error) {
      const { response } = error;

      if (response && response.data.errors) {
        setValidationErrors(response.data.errors);
        message.error('Data Contract Invalid');
      } else {
        message.error('Unable To Create Data Contract');
      }
    }
  };

  const handleFormSubmit = async formData => {
    const jsonFields = ['dataAccess', 'implementationNotes', 'sla', 'dataSchema'];
    const newContractDetails = {};
    // remove any empty strings from the form details
    // convert json fields to actual objects for post
    Object.keys(formData).forEach(key => {
      if (jsonFields.includes(key) && formData[key]) {
        newContractDetails[key] = JSON.parse(formData[key]);
      } else if (formData[key]) {
        newContractDetails[key] = formData[key];
      }
    });

    createDataContract(newContractDetails);
  };

  const handleFinishFailed = errorInfo => {
    message.error('Unable To Create Data Contract');
    console.log('Failed:', errorInfo);
  };

  const isValidJSON = () => ({
    validator({ field }, value) {
      try {
        // parse fails on empty objects. Leaving this check out would
        // cause the fields that are option to validate as if they were required

        if (field === 'dataSchema' || (field !== 'dataSchema' && !isEmpty(value))) {
          JSON.parse(value);
        }

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(new Error('Invalid JSON'));
      }
    },
  });

  let validationMessage;

  if (!isEmpty(validationErrors)) {
    validationMessage = Object.keys(validationErrors).map(field => {
      return (
        <ul key={field}>
          {validationErrors[field].map(errorMessage => (
            <li key={errorMessage}>{errorMessage.replace('dataContract.', '')}</li>
          ))}
        </ul>
      );
    });
  }

  return (
    <Modal
      title="Create Data Contract"
      style={{ top: 10 }}
      width="70%"
      onClose={handleModalClose}
      onOk={() => {}}
      onCancel={handleModalClose}
      open={isOpen}
      destroyOnClose
      data-cy="create-dataContract-modal"
      footer={[
        <Button key="close" onClick={handleModalClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          data-cy="create-contract-btn"
          type="primary"
          onClick={() => form.submit()}
        >
          Create
        </Button>,
      ]}
    >
      <Form
        initialValues={{ dataSchema: '{}', dataAccess: '{}', sla: '{}', implementationNotes: '{}' }}
        form={form}
        name="editDataContract"
        onFinish={handleFormSubmit}
        onFinishFailed={handleFinishFailed}
        autoComplete="off"
        validateTrigger="onSubmit"
        layout="vertical"
        size="small"
      >
        <Row gutter={[16, 24]}>
          <Col className="gutter-row" span={12}>
            <Form.Item name="name" label="Name" rules={[{ required: true }]}>
              <Input
                rows={1}
                placeholder="name"
                data-cy="name-input"
                maxLength={100}
                size="large"
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" span={12}>
            <Form.Item name="version" label="Version" rules={[{ required: true }]}>
              <Input
                rows={1}
                placeholder="version"
                data-cy="version-input"
                maxLength={100}
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 24]}>
          <Col className="gutter-row" span={12}>
            <Form.Item name="description" label="Description" rules={[{ required: true }]}>
              <Input
                rows={1}
                placeholder="description"
                data-cy="description-input"
                maxLength={100}
                size="large"
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" span={12}>
            <Form.Item name="provider" label="Provider" rules={[{ required: true }]}>
              <Input
                rows={1}
                placeholder="provider"
                data-cy="provider-input"
                maxLength={100}
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 24]}>
          <Col className="gutter-row" span={12}>
            <Form.Item name="dataSchema" label="Data Schema" rules={[isValidJSON]}>
              <TextArea
                allowClear
                rows={5}
                data-cy="data-schema-textarea"
                showCount
                placeholder="{}"
                maxLength={50000}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" span={12}>
            <Form.Item name="dataAccess" label="Data Access" rules={[isValidJSON]}>
              <TextArea
                allowClear
                rows={5}
                data-cy="data-access-textarea"
                showCount
                placeholder="{}"
                maxLength={50000}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 24]}>
          <Col className="gutter-row" span={12}>
            <Form.Item name="sla" label="SLA" rules={[isValidJSON]}>
              <TextArea
                allowClear
                rows={5}
                data-cy="sla-textarea"
                showCount
                placeholder="{}"
                maxLength={50000}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" span={12}>
            <Form.Item
              name="implementationNotes"
              label="Implementation Notes"
              rules={[isValidJSON]}
            >
              <TextArea
                allowClear
                rows={5}
                data-cy="implementation-notes-textarea"
                showCount
                placeholder="{}"
                maxLength={50000}
              />
            </Form.Item>
          </Col>
        </Row>
        {validationMessage && (
          <Alert
            closable
            onClose={() => setValidationErrors({})}
            message="Validation Errors"
            description={validationMessage}
            type="error"
          />
        )}
      </Form>
    </Modal>
  );
};

export default CreateDataContractModal;
